import { useEffect, useRef } from 'react';

/**
 * BACK CLOSES THE SHEET, NOT THE ROUTE (S2 part 3 — DESIGN §5.2).
 *
 * Android's back gesture and iOS's edge-swipe both fire `popstate`; without a history entry
 * of its own, an open overlay lets back pop the ROUTE (a swipe to close a picker leaves the
 * item you were editing). This gives the overlay stack ONE sentinel to swallow.
 *
 * ONE COORDINATOR, ONE SENTINEL — not one listener per overlay. An earlier per-instance
 * version gave every overlay its own `popstate` listener, so when a NESTED overlay (a picker
 * opened inside the booking form, a Confirm over a picker) consumed its own entry with
 * `history.back()`, the PARENT's listener caught that popstate and closed the parent too. So
 * instead a single module-level stack of close callbacks shares a single sentinel:
 *
 *  - Open: push the overlay's close onto the stack. Only the FIRST overlay pushes the sentinel
 *    and attaches the listener.
 *  - Back / edge-swipe → the one `popstate` closes the TOP overlay. If overlays remain, a fresh
 *    sentinel is pushed so the next back has something to pop; innermost closes first.
 *  - Close via the UI (button / scrim / Escape / an act) → the overlay leaves the stack. Only
 *    when it was the LAST one is the sentinel consumed (`history.back()`), and only if it is
 *    still on top — if the close also navigated (a booking form that lands on the new booking),
 *    `history.state` is the new route, not our sentinel, and the nav is left alone. A non-last
 *    UI close does nothing to history, so it can never fire a popstate that closes a sibling.
 *
 * `platform/router.ts` owns the only other `popstate` listener; the sentinel carries the SAME
 * url, so the router's re-parse is a no-op and this coordinator does the closing. On-device
 * gesture nuances (iOS edge-swipe not orphaning a sheet) are S6.
 */

const stack: Array<() => void> = [];
let attached = false;
/**
 * Pending self-consume `history.back()` calls to ignore. A UI close of the LAST overlay pops
 * its own sentinel with `history.back()`; that fires a `popstate` we must NOT treat as a Back
 * gesture — otherwise, when a close-then-open happens in one tick (the acts sheet closing as
 * the booking form opens), the stray popstate would close the freshly-opened overlay. The
 * listener is left attached across that transition so this counter, not a detach race, is what
 * absorbs the popstate.
 */
let suppress = 0;

function pushSentinel(): void {
  // ⚠️ CARRY THE PAGE'S DEPTH (`mk`, 0.1.524 — see `goBack` in router.ts). The sentinel is the
  // SAME screen, so it must answer "what is behind this screen" the same way; without `mk` a back
  // chip under an open sheet, or after a close that navigated, would read as a fresh visit.
  const mk = (window.history.state as { mk?: unknown } | null)?.mk;
  window.history.pushState(typeof mk === 'number' ? { __overlay: true, mk } : { __overlay: true }, '');
}

function onPop(): void {
  if (suppress > 0) {
    suppress -= 1;
    // The self-consume is done; if nothing is open any more, stop listening.
    if (stack.length === 0) detach();
    return;
  }
  const close = stack.pop();
  if (stack.length > 0) pushSentinel();
  else detach();
  close?.();
}

function attach(): void {
  if (!attached) {
    window.addEventListener('popstate', onPop);
    attached = true;
  }
}

function detach(): void {
  if (attached) {
    window.removeEventListener('popstate', onPop);
    attached = false;
  }
}

export function useHistoryDismiss(onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const close = (): void => onCloseRef.current();
    const wasEmpty = stack.length === 0;
    stack.push(close);
    if (wasEmpty) {
      pushSentinel();
      attach();
    }

    return () => {
      const i = stack.lastIndexOf(close);
      if (i === -1) return; // already removed by a gesture pop — nothing to consume
      stack.splice(i, 1);
      if (stack.length > 0) return; // siblings remain; the shared sentinel stays put
      // Last overlay, closed via the UI: consume the sentinel so the next Back is not wasted —
      // unless the close navigated, in which case history.state is the new route, not ours.
      // The listener stays attached so its own popstate is absorbed by `suppress` (detach then
      // happens in onPop); a close-then-open in the same tick keeps working.
      const state = window.history.state as { __overlay?: boolean } | null;
      if (state?.__overlay === true) {
        suppress += 1;
        window.history.back();
      } else detach();
    };
  }, []);
}

/** Test-only: clear the module-level coordinator between cases. */
export function __resetOverlayHistoryForTests(): void {
  stack.length = 0;
  suppress = 0;
  detach();
}
